import React, {useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {
	Breadcrumbs,
	Button,
	Card,
	Checkbox,
	FlexColumn,
	FlexRow,
	Link,
	OutlinedPillVariant,
	Pill,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {PageHeader} from '@/web-components/core/page-header';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import {clientLogoSrc} from '../../directApi';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {FEATURE_FLAGS} from '../../constants';
import {canReachClientsList, hasPermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import Util from '../shared/util/util';
import {showChangesSavedToast} from '../shared/components/toasts/toast';
import UpdateClientPortalConfigMutation from '../../mutations/update_client_portal_config_mutation';
import UploadClientPortalLogoMutation from '../../mutations/upload_client_portal_logo_mutation';
import WorkspaceUsersSection from './WorkspaceUsersSection';

type UploadPortalLogoResponse = {
	uploadClientPortalLogo?: {clientPortalConfig?: {portalLogoUrl?: string | null} | null} | null;
} | null;

/**
 * Accepts what both server gates accept: a 3- or 6-digit hex (the graphql proxy and
 * the portal admin router expand `#abc` to `#aabbcc`), or empty for "clear it and use
 * the Forecast default".
 *
 * Validated here as well because Save batches EVERY dirty field into ONE mutation: an
 * unvalidated bad colour fails the whole save, so the sender name and flags the user
 * also edited vanish behind a generic correlation-ID error modal. Catching it in the
 * field keeps the failure local, legible, and non-destructive.
 */
const PORTAL_COLOUR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const isValidPortalColour = (value: string): boolean => value === '' || PORTAL_COLOUR_RE.test(value);

type ClientHeaderData = {
	id: string;
	name: string;
	logoId: string | null;
	status: {label: string; standing: 'ACTIVE' | 'INACTIVE'} | null;
};

type PortalConfigData = {
	portalActive: boolean | null;
	portalLogoUrl: string | null;
	portalPrimaryColour: string | null;
	portalSenderName: string | null;
	showInvoices: boolean | null;
	allowFileUpload: boolean | null;
	showRetainer: boolean | null;
	showComments: boolean | null;
	hideTimeEntries: boolean | null;
	hideEstimates: boolean | null;
};

/**
 * Preset brand colours offered beside the hex field. These are *data* — values
 * written to this client's workspace config and rendered by the client's portal, not
 * styling for this page — so they are literal hex rather than theme tokens. The list
 * is a shortcut for the common choices; any valid hex can still be typed.
 */
const BRAND_COLOUR_PRESETS = ['#0004db', '#0066ff', '#148f6a', '#e57700', '#232334'];

const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
`;

// Header sits above the content with the same 32px horizontal rhythm as the Overview and
// Opportunities tabs.
const HeaderBar = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 8px 32px 16px 32px;
`;

const TitleRow = Styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const ClientLogo = Styled.img`
	width: 32px;
	height: 32px;
	border-radius: 6px;
	object-fit: cover;
	flex-shrink: 0;
`;

// Pushes the client's status pill to the far end of the title row.
const StatusSlot = Styled.div`
	margin-left: auto;
`;

// Bordered surface the setting rows sit in. The rows carry their own top hairline, so
// the card itself adds only the outer border and clips the first row's corners.
const SettingsCard = Styled.div`
	border: 1px solid ${props => props.theme.sys.color.border.decorative};
	border-radius: 8px;
	background-color: ${props => props.theme.sys.color.background.neutral['1']};
	overflow: hidden;

	/* The band or row at the very top needs no separator of its own. */
	> *:first-child {
		border-top: none;
	}
`;

// A settings row: name + explanation on the left, the single control on the right,
// hairline-separated from the row above so a column of them reads as one list of
// decisions rather than a run-on form.
const SettingsRow = Styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
	padding: 16px 20px;
	border-top: 1px solid ${props => props.theme.sys.color.border.decorative};
`;

const RowText = Styled.div`
	max-width: 560px;
`;

const RowLabel = Styled.div`
	${props => props.theme.sys.font.subheading['2']};
	color: ${props => props.theme.sys.color.text.content.main};
	margin-bottom: 4px;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
`;

const RowHelp = Styled.div`
	${props => props.theme.sys.font.body['2']};
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

const RowControl = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
`;

// Groups the rows below it under one banner ("Branding", "Feature flags") so the card
// scans as sections instead of one long list.
const SubsectionBand = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 14px 20px;
	border-top: 1px solid ${props => props.theme.sys.color.border.decorative};
	background-color: ${props => props.theme.sys.color.background.neutral['2']};
	${props => props.theme.sys.font.subheading['2']};
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

const SubsectionNote = Styled.span`
	${props => props.theme.sys.font.detail['1']};
	letter-spacing: normal;
	text-transform: none;
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

// The flag's backend key, shown beside its label so a firm admin comparing this
// screen against support notes or the API sees the same identifier.
const FlagKey = Styled.code`
	${props => props.theme.sys.font.detail['1']};
	color: ${props => props.theme.sys.color.text.content.auxiliary};
	background-color: ${props => props.theme.sys.color.background.neutral['2']};
	border: 1px solid ${props => props.theme.sys.color.border.decorative};
	border-radius: 4px;
	padding: 2px 6px;
`;

const SwatchRow = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

// Selected preset gets a dark ring; the thin outer ring keeps a white or very light
// swatch visible against the card.
const Swatch = Styled.button<{$colour: string; $selected: boolean}>`
	width: 30px;
	height: 30px;
	padding: 0;
	border-radius: 8px;
	cursor: pointer;
	background-color: ${({$colour}) => $colour};
	border: 2px solid ${({theme, $selected}) => ($selected ? theme.sys.color.text.content.main : 'transparent')};
	box-shadow: 0 0 0 1px ${props => props.theme.sys.color.border.decorative};

	&:disabled {
		cursor: default;
	}
`;

// Says where a saved change lands and when the client will see it.
const AuditNote = Styled.div`
	${props => props.theme.sys.font.detail['1']};
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

const SectionHeading = Styled.div`
	${props => props.theme.sys.font.heading['2']};
	color: ${props => props.theme.sys.color.text.content.main};
`;

const SectionHeadingRow = Styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

const ContentArea = Styled.div`
	padding: 0 32px 32px 32px;
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
`;

// The portal logo preview is capped rather than stretched: firms upload wordmarks of wildly
// different aspect ratios and the picker is a preview, not the portal's own rendering.
const PortalLogoPreview = Styled.img`
	max-height: 48px;
	max-width: 200px;
	object-fit: contain;
`;

const HiddenFileInput = Styled.input`
	display: none;
`;

type WorkspaceHeaderProps = {
	client: ClientHeaderData | null;
	clientId: string;
};

// Breadcrumb (All Clients › <client> › Workspace) + client logo/name/status, matching the
// client-detail page chrome. The final crumb is the current page, so it is plain text; the client
// name links back to the client Overview.
const WorkspaceHeader = ({client, clientId}: WorkspaceHeaderProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	const clientsListPath = hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENTS_FEATURE) ? '/clients' : '/admin/clients';
	// Same gate as the client page's own header. Only the first crumb goes — the
	// client-name crumb stays, and is the route back to Overview.
	const canReachClients = canReachClientsList();
	const clientName = client?.name ?? '';
	const status = client?.status ?? null;
	// Keep the href for accessibility / open-in-new-tab, but do a client-side push on a plain
	// left-click so the crumbs navigate within the SPA instead of triggering a full page reload.
	const spaNavigate = (path: string) => (event: React.MouseEvent) => {
		if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
			event.preventDefault();
			history.push(path);
		}
	};
	return (
		<HeaderBar>
			<Breadcrumbs>
				{canReachClients && (
					<Breadcrumbs.Item>
						<Link href={clientsListPath} onClick={spaNavigate(clientsListPath)}>
							{formatMessage({id: 'company_view_page.breadcrumb_all_clients'})}
						</Link>
					</Breadcrumbs.Item>
				)}
				<Breadcrumbs.Item>
					<Link href={`/clients/${clientId}`} onClick={spaNavigate(`/clients/${clientId}`)}>
						{clientName}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<span>{formatMessage({id: 'company_view_page.nav_workspace'})}</span>
				</Breadcrumbs.Item>
			</Breadcrumbs>
			<TitleRow data-cy="company-workspace-title">
				{client?.logoId ? (
					<ClientLogo crossOrigin="use-credentials" data-private src={clientLogoSrc(client.logoId)} alt="" />
				) : null}
				<PageHeader>{clientName}</PageHeader>
				{status ? (
					// Outlined pill with a status dot, so the standing is carried by a design-token
					// colour rather than a hex literal that drifts from the palette (and from the
					// Sage theme, which has its own).
					<StatusSlot data-cy="company-workspace-client-status">
						<Pill
							outlined
							outlinedVariant={
								status.standing === 'ACTIVE' ? OutlinedPillVariant.SUCCESS : OutlinedPillVariant.NEUTRAL
							}
						>
							{status.label}
						</Pill>
					</StatusSlot>
				) : null}
			</TitleRow>
		</HeaderBar>
	);
};

type WorkspaceSettingsProps = {
	clientId: string;
	clientName: string;
	portalConfig: PortalConfigData;
	/** When the workspace is inactive the fields render but cannot be edited or saved. */
	disabled: boolean;
};

// Branding + feature flags for this client's workspace. Edited locally and committed on an explicit
// Save, mirroring the firm-wide Client Workspace settings page. The logo is the exception: it is a
// binary mutation of its own and uploads the moment a file is picked, independent of Save.
const WorkspaceSettings = ({clientId, clientName, portalConfig, disabled}: WorkspaceSettingsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const portalLogoInputRef = useRef<HTMLInputElement>(null);

	// allowFileUpload is ON-by-default in the portal's effective-permissions view, so an
	// unconfigured client must render CHECKED — otherwise the box reads "uploads off" while
	// uploads are actually permitted. Derived once and reused by the dirty check below: if the
	// seed and the baseline disagree, the form is dirty on open (or never dirty when toggled back).
	const portalAllowFileUploadInitial = portalConfig.allowFileUpload ?? true;

	// show_retainer is ON-by-default in the portal's effective-permissions view, so an
	// absent flag row must read as checked — and the dirty-check must compare against
	// that same default or tick-then-untick writes nothing.
	const portalShowRetainerInitial = portalConfig.showRetainer ?? true;

	// show_comments is ON-by-default in the portal's effective-permissions view, so an
	// absent flag row must read as checked — and the dirty check must compare against
	// that same default or tick-then-untick writes nothing.
	const portalShowCommentsInitial = portalConfig.showComments ?? true;

	const [portalPrimaryColour, setPortalPrimaryColour] = useState<string>(portalConfig.portalPrimaryColour || '');
	// Set on a rejected Save, cleared as soon as the user edits the field again, so the
	// message appears when they act on it and disappears when they start fixing it.
	const [portalPrimaryColourError, setPortalPrimaryColourError] = useState<boolean>(false);
	const [portalSenderName, setPortalSenderName] = useState<string>(portalConfig.portalSenderName || '');
	const [portalShowInvoices, setPortalShowInvoices] = useState<boolean>(!!portalConfig.showInvoices);
	const [portalAllowFileUpload, setPortalAllowFileUpload] = useState<boolean>(portalAllowFileUploadInitial);
	const [portalShowRetainer, setPortalShowRetainer] = useState<boolean>(portalShowRetainerInitial);
	const [portalShowComments, setPortalShowComments] = useState<boolean>(portalShowCommentsInitial);
	const [portalHideTimeEntries, setPortalHideTimeEntries] = useState<boolean>(!!portalConfig.hideTimeEntries);
	const [portalHideEstimates, setPortalHideEstimates] = useState<boolean>(!!portalConfig.hideEstimates);
	// Mirrors portalConfig.portalLogoUrl, re-pointed on a successful upload.
	const [portalLogoUrl, setPortalLogoUrl] = useState<string | null>(portalConfig.portalLogoUrl ?? null);

	const onClickChangePortalLogo = () => portalLogoInputRef.current?.click();

	const onPortalLogoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];
		// Clear the input so re-picking the same file still fires a change event — otherwise
		// retrying a rejected upload is silently inert.
		e.target.value = '';
		if (!file) return;
		// File rides as an uploadable — not a GraphQL variable — so the mutation reads it off the
		// multipart request body rather than from args.
		Util.CommitMutation(
			UploadClientPortalLogoMutation,
			{clientOrgId: String(clientId)},
			(response: UploadPortalLogoResponse) => {
				// ClientPortalConfig carries no id, so Relay cannot normalize the payload back
				// onto client.portalConfig. Track the fresh URL locally so the preview updates
				// without a page reload.
				const url = response?.uploadClientPortalLogo?.clientPortalConfig?.portalLogoUrl;
				if (url) setPortalLogoUrl(url);
				showChangesSavedToast(formatMessage);
			},
			true,
			{file}
		);
	};

	const onColourChange = (value: string) => {
		setPortalPrimaryColour(value);
		if (portalPrimaryColourError) setPortalPrimaryColourError(false);
	};

	const onClickSave = () => {
		// Refuse the save rather than letting one bad colour take the whole batched
		// mutation (and every other edit in it) down with a generic error modal.
		if (!isValidPortalColour(portalPrimaryColour)) {
			setPortalPrimaryColourError(true);
			return;
		}
		// Only the fields the user actually touched are sent. An omitted field is left untouched by
		// the portal, so a narrow payload avoids re-asserting values this form never owned — and an
		// entirely clean form must not write a config row for a client the portal has never seen.
		const changed: Record<string, string | boolean | null> = {};
		if ((portalPrimaryColour || '') !== (portalConfig.portalPrimaryColour || '')) {
			changed.portalPrimaryColour = portalPrimaryColour || null;
		}
		if ((portalSenderName || '') !== (portalConfig.portalSenderName || '')) {
			changed.portalSenderName = portalSenderName || null;
		}
		if (portalShowInvoices !== !!portalConfig.showInvoices) changed.showInvoices = portalShowInvoices;
		if (portalAllowFileUpload !== portalAllowFileUploadInitial) changed.allowFileUpload = portalAllowFileUpload;
		if (portalShowRetainer !== portalShowRetainerInitial) changed.showRetainer = portalShowRetainer;
		if (portalShowComments !== portalShowCommentsInitial) changed.showComments = portalShowComments;
		if (portalHideTimeEntries !== !!portalConfig.hideTimeEntries) changed.hideTimeEntries = portalHideTimeEntries;
		if (portalHideEstimates !== !!portalConfig.hideEstimates) changed.hideEstimates = portalHideEstimates;

		if (Object.keys(changed).length === 0) return;

		Util.CommitMutation(
			UpdateClientPortalConfigMutation,
			{
				clientOrgId: String(clientId),
				// Forwarded so the portal can name the client org it provisions on a first-touch
				// write; it seeds a new row only and never renames an org the portal already knows.
				clientName,
				...changed,
			},
			() => showChangesSavedToast(formatMessage)
		);
	};

	// The flag rows, in the order the design lists them, with the two Forecast-only
	// flags (retainer, comments) appended. Each carries the backend key so the row can
	// show it beside the label, and its own accessible name — the visible label lives in
	// the row's left column, so the control is named through aria-label rather than the
	// Checkbox's own labelText, which would print the name a second time.
	const flagRows = [
		{
			key: 'show_invoices',
			labelId: 'client_portal.flag_show_invoices',
			helpId: 'client_portal.flag_show_invoices_help',
			checked: portalShowInvoices,
			toggle: () => setPortalShowInvoices(p => !p),
		},
		{
			key: 'allow_file_upload',
			labelId: 'client_portal.flag_allow_file_upload',
			helpId: 'client_portal.flag_allow_file_upload_help',
			checked: portalAllowFileUpload,
			toggle: () => setPortalAllowFileUpload(p => !p),
		},
		{
			key: 'hide_time_entries',
			labelId: 'client_portal.flag_hide_time_entries',
			helpId: 'client_portal.flag_hide_time_entries_help',
			checked: portalHideTimeEntries,
			toggle: () => setPortalHideTimeEntries(p => !p),
		},
		{
			key: 'hide_estimates',
			labelId: 'client_portal.flag_hide_estimates',
			helpId: 'client_portal.flag_hide_estimates_help',
			checked: portalHideEstimates,
			toggle: () => setPortalHideEstimates(p => !p),
		},
		{
			key: 'show_retainer',
			labelId: 'client_portal.flag_show_retainer',
			helpId: 'client_portal.flag_show_retainer_help',
			checked: portalShowRetainer,
			toggle: () => setPortalShowRetainer(p => !p),
		},
		{
			key: 'show_comments',
			labelId: 'client_portal.flag_show_comments',
			helpId: 'client_portal.flag_show_comments_help',
			checked: portalShowComments,
			toggle: () => setPortalShowComments(p => !p),
		},
	];

	return (
		<FlexColumn gap={'m'} data-cy="workspace-settings">
			<SettingsCard>
				<SubsectionBand>{formatMessage({id: 'client_portal.section_branding'})}</SubsectionBand>

				<SettingsRow>
					<RowText>
						<RowLabel>{formatMessage({id: 'client_portal.logo'})}</RowLabel>
						<RowHelp>{formatMessage({id: 'client_portal.logo_help'})}</RowHelp>
					</RowText>
					<RowControl>
						{portalLogoUrl ? (
							// The portal serves this from object storage as a pre-signed URL, so the
							// browser must NOT attach credentials (the signature is the auth, and the
							// bucket sends no CORS headers). data-private still keeps the src out of
							// LogRocket recordings.
							<PortalLogoPreview
								data-testid="portal-logo-preview"
								src={portalLogoUrl}
								alt={formatMessage({id: 'client_portal.logo'})}
								data-private
							/>
						) : (
							<Text color={'secondary'}>{formatMessage({id: 'client_portal.logo_default'})}</Text>
						)}
						<HiddenFileInput
							data-testid="portal-logo-input"
							type="file"
							ref={portalLogoInputRef}
							accept="image/png,image/jpeg,image/webp"
							onChange={onPortalLogoSelected}
							disabled={disabled}
							aria-label={formatMessage({id: 'client_portal.logo'})}
						/>
						<Button emphasis="low" onPress={onClickChangePortalLogo} disabled={disabled}>
							{formatMessage({id: portalLogoUrl ? 'clients.update-logo' : 'clients.upload-logo'})}
						</Button>
					</RowControl>
				</SettingsRow>

				<SettingsRow>
					<RowText>
						<RowLabel>{formatMessage({id: 'client_portal.primary_colour'})}</RowLabel>
						<RowHelp>{formatMessage({id: 'client_portal.primary_colour_help'})}</RowHelp>
					</RowText>
					<RowControl>
						<SwatchRow>
							{BRAND_COLOUR_PRESETS.map(preset => (
								<Swatch
									key={preset}
									type="button"
									title={preset}
									aria-label={preset}
									$colour={preset}
									$selected={portalPrimaryColour.toLowerCase() === preset}
									disabled={disabled}
									onClick={() => onColourChange(preset)}
								/>
							))}
						</SwatchRow>
						<TextField
							value={portalPrimaryColour}
							onValueChange={onColourChange}
							disabled={disabled}
							hasError={portalPrimaryColourError}
							errorText={
								portalPrimaryColourError
									? formatMessage({id: 'client_portal.primary_colour_invalid'})
									: undefined
							}
							aria-label={formatMessage({id: 'client_portal.primary_colour'})}
						/>
					</RowControl>
				</SettingsRow>

				<SettingsRow>
					<RowText>
						<RowLabel>{formatMessage({id: 'client_portal.sender_name'})}</RowLabel>
						<RowHelp>{formatMessage({id: 'client_portal.sender_name_help'})}</RowHelp>
					</RowText>
					<RowControl>
						<TextField
							value={portalSenderName}
							onValueChange={setPortalSenderName}
							disabled={disabled}
							aria-label={formatMessage({id: 'client_portal.sender_name'})}
						/>
					</RowControl>
				</SettingsRow>

				<SubsectionBand>
					{formatMessage({id: 'client_portal.section_feature_flags'})}
					<SubsectionNote>{formatMessage({id: 'client_portal.feature_flags_override_note'})}</SubsectionNote>
				</SubsectionBand>

				{flagRows.map(flag => {
					const label = formatMessage({id: flag.labelId});
					return (
						<SettingsRow key={flag.key}>
							<RowText>
								<RowLabel>
									{label}
									<FlagKey>{flag.key}</FlagKey>
								</RowLabel>
								<RowHelp>{formatMessage({id: flag.helpId})}</RowHelp>
							</RowText>
							<RowControl>
								<Checkbox
									checked={flag.checked}
									onChange={flag.toggle}
									disabled={disabled}
									aria-label={label}
									data-cy={`workspace-flag-${flag.key}`}
								/>
							</RowControl>
						</SettingsRow>
					);
				})}
			</SettingsCard>

			<AuditNote>{formatMessage({id: 'client_portal.client_org_audit_note'})}</AuditNote>

			<FlexRow>
				<Button onPress={onClickSave} disabled={disabled} data-cy="workspace-save">
					{formatMessage({id: 'common.save', defaultMessage: 'Save'})}
				</Button>
			</FlexRow>
		</FlexColumn>
	);
};

type RawPortalUser = {
	readonly id: string | null;
	readonly email: string | null;
	readonly clientContactId: string | null;
	readonly roleKey: string | null;
	readonly status: string | null;
	readonly scopeLevel: string | null;
	readonly scopeProgrammeIds: ReadonlyArray<string | null> | null;
	readonly scopeProjectIds: ReadonlyArray<string | null> | null;
};

type RawHierarchyNode = {
	readonly id: string | null;
	readonly name: string | null;
	readonly programmeId?: string | null;
	readonly published?: boolean | null;
};

type RawClientContact = {
	readonly id: string | null;
	readonly firstName: string | null;
	readonly lastName: string | null;
	readonly email: string | null;
};

type ClientUsersData = {
	readonly portalUsers?: ReadonlyArray<RawPortalUser | null> | null;
	readonly clientPortalHierarchy?: {
		readonly programmes: ReadonlyArray<RawHierarchyNode | null> | null;
		readonly projects: ReadonlyArray<RawHierarchyNode | null> | null;
	} | null;
	readonly clientContacts?: {
		readonly edges: ReadonlyArray<{readonly node: RawClientContact | null} | null> | null;
	} | null;
};

type WorkspaceContentProps = {
	clientId: string;
	client: (ClientHeaderData & {portalConfig: PortalConfigData | null} & ClientUsersData) | null;
	refetchClient?: () => void;
};

const WorkspaceContent = ({clientId, client, refetchClient}: WorkspaceContentProps): JSX.Element => {
	const {formatMessage} = useIntl();
	// Presentation only — the write is gated server-side on the same permission. Without it the
	// portal fields resolve to null rather than erroring, so an unauthorized viewer would otherwise
	// be shown an empty form they cannot save.
	const canManageSettings = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	const portalConfig = client?.portalConfig ?? null;

	if (!canManageSettings) {
		return (
			<ContentArea>
				<Text data-cy="workspace-no-permission">
					{formatMessage({
						id: 'client_portal.insufficient_permission',
						defaultMessage: 'You do not have permission to manage this workspace.',
					})}
				</Text>
			</ContentArea>
		);
	}

	// The workspace is activated from the Edit Client dialog, not here, so an inactive workspace
	// shows its settings read-only rather than hiding them: an admin can see what is configured
	// before deciding to turn it on.
	const isActive = !!portalConfig?.portalActive;

	return (
		<ContentArea>
			<FlexColumn gap={'l'}>
				{/* Section header carries the workspace's own on/off standing as a pill. The
				    control that changes it lives in the Edit Client dialog, so this reflects
				    the persisted value and never implies it can be toggled from here. */}
				<FlexColumn gap={'s'}>
					<SectionHeadingRow>
						<SectionHeading>
							{formatMessage({id: 'client_portal.section_title', defaultMessage: 'Client Workspace'})}
						</SectionHeading>
						<span data-cy="workspace-active-status">
							<Pill
								outlined
								outlinedVariant={isActive ? OutlinedPillVariant.SUCCESS : OutlinedPillVariant.NEUTRAL}
							>
								{formatMessage({id: isActive ? 'common.active' : 'common.inactive'})}
							</Pill>
						</span>
					</SectionHeadingRow>
					<RowHelp>{formatMessage({id: 'client_portal.client_org_desc'})}</RowHelp>
					{!isActive && (
						<Text color={'secondary'} data-cy="workspace-inactive-prompt">
							{formatMessage({
								id: 'client_portal.activate_prompt_workspace_tab',
								defaultMessage:
									'This workspace is inactive. Activate it in the Edit Client dialog to apply these settings.',
							})}
						</Text>
					)}
				</FlexColumn>
				<WorkspaceSettings
					clientId={clientId}
					clientName={client?.name ?? ''}
					portalConfig={
						portalConfig ?? {
							portalActive: false,
							portalLogoUrl: null,
							portalPrimaryColour: null,
							portalSenderName: null,
							showInvoices: null,
							allowFileUpload: null,
							showRetainer: null,
							showComments: null,
							hideTimeEntries: null,
							hideEstimates: null,
						}
					}
					disabled={!isActive}
				/>

				{/* Portal-user provisioning / invitation / deactivation for this client. Only shown when
				    the workspace is active — activation happens in the Edit Client dialog, and this page
				    refetches on mount, so the persisted value is the truth. No live-toggle prop passed:
				    WorkspaceUsersSection gates itself on the persisted portalConfig.portalActive. */}
				{client && !!client.portalConfig?.portalActive && (
					<Card>
						<Card.Title>{formatMessage({id: 'client_workspace.users.title'})}</Card.Title>
						<Card.Body>
							<WorkspaceUsersSection client={client} refetchClient={refetchClient} />
						</Card.Body>
					</Card>
				)}
			</FlexColumn>
		</ContentArea>
	);
};

export const CompanyViewWorkspaceQuery = graphql`
	query CompanyViewWorkspace_Query($clientId: String) {
		viewer {
			actualPersonId
			company {
				id
				client(id: $clientId) {
					id
					name
					logoId
					status {
						label
						standing
					}
					portalConfig {
						portalActive
						portalLogoUrl
						portalPrimaryColour
						portalSenderName
						showInvoices
						allowFileUpload
						showRetainer
						showComments
						hideTimeEntries
						hideEstimates
					}
					portalUsers {
						id
						email
						clientContactId
						roleKey
						status
						scopeLevel
						scopeProgrammeIds
						scopeProjectIds
					}
					clientPortalHierarchy {
						programmes {
							id
							name
						}
						projects {
							id
							name
							programmeId
							published
						}
					}
					clientContacts(first: 1000) {
						edges {
							node {
								id
								firstName
								lastName
								email
							}
						}
					}
				}
			}
		}
	}
`;

type Props = {
	clientId: string;
};

// The client-detail Workspace tab: this client's Customer Portal configuration. Owns its own query
// boundary. The default store-and-network policy matters here — activating the workspace happens in
// the Edit Client dialog and its mutation payload cannot be normalized onto client.portalConfig, so
// the tab has to refetch on mount to see a just-activated workspace.
export const CompanyViewWorkspace = ({clientId}: Props): JSX.Element => {
	return (
		<ForecastQueryRenderer
			key={'query-render-company-workspace-' + clientId}
			query={CompanyViewWorkspaceQuery}
			variables={{clientId}}
			render={(relayProps, retry) => {
				const client = relayProps?.viewer?.company?.client ?? null;
				return (
					<PageContent>
						<WorkspaceHeader client={client} clientId={clientId} />
						<WorkspaceContent clientId={clientId} client={client} refetchClient={retry} />
					</PageContent>
				);
			}}
		/>
	);
};

export default CompanyViewWorkspace;
